// PaymentForm.js
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert'; // 导入 ErrorAlert 组件
import { Container, Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, Button, CssBaseline, FormControl, FormLabel, Grid } from '@mui/material'; // Import Material UI components

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false); //  控制 ErrorAlert 组件显示
  const [paymentType, setPaymentType] = useState('one-time'); //  支付类型状态
  const location = useLocation(); //  使用 useLocation 获取 location 对象
  const navigate = useNavigate(); //  使用 useNavigate 进行页面跳转
  const [planId, setPlanId] = useState(location.state?.planId); //  从 location.state 中获取 planId
  const [planName, setPlanName] = useState(location.state?.planName); //  获取 planName
  const [planPrice, setPlanPrice] = useState(location.state?.planPrice); //  获取 planPrice
  const [paymentMethodType, setPaymentMethodType] = useState('credit_card'); // 新增支付方式类型 state, 默认信用卡
  const [loading, setLoading] = useState(false); //  新增 loading 状态
  const [applePayAvailable, setApplePayAvailable] = useState(false); //  Apple Pay 可用性状态

  const handleCloseErrorAlert = () => { //  关闭错误提示框
    setShowErrorAlert(false);
    setError(null); //  同时清空错误信息
  };

  useEffect(() => {
    if (paymentType === 'one-time') {
        setPlanId(null);
    } else if (paymentType === 'subscription' && !planId) {
        console.warn('No plan selected for subscription payment.');
    }
  }, [paymentType, planId, location.state?.planId]);

  useEffect(() => {
    //  Check for Apple Pay availability on component mount
    async function checkApplePayAvailability() {
      if (window.ApplePaySession) {
        const isAvailable = await window.ApplePaySession.canMakePayments(); // 使用 window.ApplePaySession
        setApplePayAvailable(isAvailable);
      }
    }
    checkApplePayAvailability();
  }, []);
  

  const onApplePayButtonClicked = async (event) => { //  Apple Pay 按钮点击事件处理函数
    event.preventDefault();
    setError(null);
    setShowErrorAlert(false);
    setLoading(true);

    if (!stripe) {
        setError('Stripe.js 尚未加载完成，请稍后重试');
        setShowErrorAlert(true);
        setLoading(false);
        return;
    }

    //  创建 Apple Pay Payment Request
    const paymentRequest = stripe.paymentRequest({
        country: 'US', //  国家代码 -  根据你的 Stripe 账户所在国家设置
        currency: 'usd', //  货币代码 -  根据你的业务货币设置
        total: {
            label: planName || '总计', //  订单总计标签
            amount: parseFloat(planPrice) || 100, //  订单总计金额 (示例金额 100 USD，实际应从 planPrice 或 state 中获取)
        },
        //  requestPayerName: true, //  是否请求付款人姓名
        //  requestPayerEmail: true, //  是否请求付款人邮箱
    });

    //  检查 Payment Request 是否有效
    if (!paymentRequest) {
        setError('Apple Pay Payment Request 创建失败，请检查配置');
        setShowErrorAlert(true);
        setLoading(false);
        return;
    }


    paymentRequest.on('validatemerchant', async (event) => { //  Merchant Validation 事件处理
        console.log('Apple Pay onvalidatemerchant event:', event); //  日志

        try {
            //  调用后端 API 进行 Merchant Validation
            const validationResponse = await axios.post('/payments/applepay/merchant-validation', { //  调用后端 Merchant Validation 接口
                validationURL: event.validationURL, //  Apple Pay 提供的 validationURL
                //  merchantIdentifier, displayName, domainName  -  这些信息通常 Stripe 后端会自动处理，前端无需显式传递
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`, //  携带 token (如果需要)
                },
            });

            const merchantSession = validationResponse.data.merchantSession; //  从后端响应中获取 Merchant Session 对象
            console.log('Apple Pay Merchant Session from backend:', merchantSession); //  日志

            event.complete(merchantSession); //  完成 Merchant Validation

        } catch (validationError) {
            console.error('Apple Pay Merchant Validation 失败:', validationError);
            event.abort(); //  Merchant Validation 失败，中止 Apple Pay Session
            let errorMessage = 'Apple Pay Merchant Validation 失败，请稍后重试'; // 默认错误信息
            if (validationError.response) {
                //  根据 HTTP 状态码提供更具体的错误提示
                if (validationError.response.status === 400) {
                    errorMessage = validationError.response.data.message || 'Merchant Validation 请求参数错误'; //  例如，后端验证错误
                } else if (validationError.response.status >= 500) {
                    errorMessage = 'Merchant Validation 服务器繁忙，请稍后重试'; //  服务器错误
                } else {
                    errorMessage = `Merchant Validation 失败: ${validationError.response.status} - ${validationError.response.statusText}`; //  其他 HTTP 错误
                }
            } else if (validationError.request) {
                errorMessage = '无法连接到 Merchant Validation 服务器，请检查您的网络连接'; //  网络错误
            }
            setError(errorMessage); //  设置更具体的错误信息
            setShowErrorAlert(true);
        } finally {
            setLoading(false); //  结束加载 (无论成功或失败)
        }
    });

    paymentRequest.on('paymentauthorized', async (event) => { //  Payment Authorized 事件处理
      console.log('Apple Pay onpaymentauthorized event:', event); //  日志

      setLoading(true); //  开始加载

      try {
          //  将 payment event 数据发送到后端进行支付处理
          const paymentResponse = await axios.post('/payments/applepay/process-payment', { //  调用后端支付处理接口
              paymentData: event.payment, //  将 payment event 数据发送到后端
              paymentType: paymentType, //  传递 paymentType (one-time 或 subscription)
              planId: planId, //  传递 planId (如果是订阅)
              //  amount: planPrice  - 金额信息应该在后端安全地获取，前端不传递敏感信息
          }, {
              headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`, //  携带 token
              },
          });

          const paymentResult = paymentResponse.data; //  假设后端返回 { success: true/false, message: '...', ... }
          console.log('Apple Pay Payment Result from backend:', paymentResult); // 日志

          if (paymentResult.success) {
              event.complete({ result: 'success' }); //  Apple Pay sheet 中显示支付成功
              alert('Apple Pay 支付成功！'); //  成功提示 (可以替换为更友好的 UI 提示)
              navigate('/profile'); //  支付成功后跳转到个人资料页
          } else {
              event.complete({ result: 'failure' }); // Apple Pay sheet 中显示支付失败
              let errorMessage = paymentResult.message || 'Apple Pay 支付失败，请稍后重试'; // 默认错误信息，优先使用后端返回的 message
              setError(errorMessage);
              setShowErrorAlert(true);
              alert('Apple Pay 支付失败: ' + errorMessage);
          }


      } catch (error) {
        console.error('Apple Pay Payment Processing Error:', error);
        event.complete({ result: 'failure' });
        let errorMessage = 'Apple Pay 支付处理失败，请稍后重试'; // 默认错误信息
        if (error.response) {
            //  根据 HTTP 状态码提供更具体的错误提示
            if (error.response.status === 400 || error.response.status === 402) { //  402 Payment Required 可能是 Stripe 返回的支付失败状态码
                errorMessage = error.response.data.message || 'Apple Pay 支付被拒绝，请检查您的支付信息或尝试其他支付方式'; //  支付被拒绝
            } else if (error.response.status >= 500) {
                errorMessage = 'Apple Pay 支付服务器繁忙，请稍后重试'; //  服务器错误
            } else {
                errorMessage = `Apple Pay 支付处理失败: ${error.response.status} - ${error.response.statusText}`; //  其他 HTTP 错误
            }
        } else if (error.request) {
            errorMessage = '无法连接到 Apple Pay 支付服务器，请检查您的网络连接'; //  网络错误
        }
        setError(errorMessage);
        setShowErrorAlert(true);
        alert('Apple Pay 支付处理失败: ' + errorMessage);
      } finally {
          setLoading(false); //  结束加载
      }
  });

    paymentRequest.on('paymentmethodselected', (event) => { //  Payment Method Selected 事件处理
        console.log('Apple Pay onpaymentmethodselected event:', event); //  日志
        //  可以根据用户选择的支付方式 (卡类型等) 进行一些处理 (可选)
    });

    paymentRequest.on('cancel', () => { //  Payment Cancel 事件处理
        console.log('Apple Pay payment cancelled by user'); //  日志
        setLoading(false); //  结束加载
        //  用户取消支付，可以进行一些清理操作或提示
    });

    paymentRequest.canMakePayment().then(canMakePayment => { //  检查是否可以发起 Apple Pay
        if (canMakePayment) {
            paymentRequest.show(); //  显示 Apple Pay 支付 sheet
        } else {
            setError('无法使用 Apple Pay 完成支付，请检查您的 Apple Pay 设置');
            setShowErrorAlert(true);
            setLoading(false); //  结束加载
        }
    }).catch(err => {
        console.error('Error checking canMakePayment:', err);
        setError('检查 Apple Pay 可用性时出错，请稍后重试');
        setShowErrorAlert(true);
        setLoading(false); //  结束加载
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setShowErrorAlert(false); //  隐藏错误提示框
    setLoading(true); //  开始加载，显示加载指示器

    if (paymentMethodType === 'credit_card') { //  处理信用卡支付 (Stripe)
      if (!stripe || !elements) {
          return;
      }

      const cardElement = elements.getElement(CardElement);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
      });

      if (error) {
          setError(error.message);
          setShowErrorAlert(true);
          setLoading(false); //  结束加载
      } else {
          try {
              let paymentResponse;
              if (paymentType === 'one-time') {
                  paymentResponse = await axios.post(
                      '/payments/process',
                      {
                          amount: 100,
                          paymentMethodId: paymentMethod.id,
                          paymentMethodType: paymentMethodType, // 传递 paymentMethodType
                      },
                      {
                          headers: {
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                          },
                      }
                  );
                  // ... 处理 Stripe 一次性支付结果
              } else if (paymentType === 'subscription') {
                  if (!planId) {
                      setError('请选择订阅套餐');
                      setShowErrorAlert(true);
                      setLoading(false); //  结束加载 (失败)
                      return;
                  }
                  paymentResponse = await axios.post(
                      '/payments/subscribe',
                      {
                          priceId: planId,
                          paymentMethodId: paymentMethod.id,
                          paymentMethodType: paymentMethodType, // 传递 paymentMethodType
                      },
                      {
                          headers: {
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                          },
                      }
                  );
                  // ... 处理 Stripe 订阅结果
              }
              setLoading(false); //  结束加载 (成功)
              // ... 支付成功后跳转

          } catch (error) {
              setError(error.response?.data?.message || 'An error occurred');
              setShowErrorAlert(true);
              setLoading(false); //  结束加载 (失败)
              alert('信用卡支付失败: ' + (error.response?.data?.message || 'An error occurred'));
              // alert('支付失败: ' + (error.response?.data?.message || 'An error occurred'));

          }
      }
    }  else if (paymentMethodType === 'alipay'|| paymentMethodType === 'wechatpay') { //  同时处理 Alipay 和 WeChat Pay
      try {
          let paymentResponse;
          if (paymentType === 'one-time') {
              paymentResponse = await axios.post(
                  '/payments/alipay/create-order', //  调用后端 Alipay 创建订单接口 //  复用 /payments/alipay/create-order 接口，后端根据 paymentType 区分
                  {
                      amount: 100, //  示例金额，实际应从 props 或 state 获取
                      paymentType: paymentType,
                      paymentMethodType: paymentMethodType, //  传递 paymentMethodType
                  },
                  {
                      headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`,
                      },
                  }
              );
          } else if (paymentType === 'subscription') {
              if (!planId) {
                  setError('请选择订阅套餐');
                  setShowErrorAlert(true);
                  setLoading(false); //  结束加载 (失败 - 前端验证)
                  return;
              }
              paymentResponse = await axios.post(
                  '/payments/alipay/create-order', //  复用创建订单接口，后端根据 paymentType 和是否订阅来处理  复用 /payments/alipay/create-order 接口，后端根据 paymentType 区分
                  {
                      priceId: planId,
                      paymentType: paymentType,
                      paymentMethodType: paymentMethodType, //  传递 paymentMethodType
                  },
                  {
                      headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`,
                      },
                  }
              );
          }


          const paymentUrl = paymentResponse.data.paymentUrl; //   从响应中获取 paymentUrl (Alipay 或 WeChat Pay)
          if (paymentUrl) {
              setLoading(false); //  在跳转前结束加载
              window.location.href = paymentUrl; //  前端跳转到支付宝支付页面
              return; //  重要：阻止后续代码执行，避免重复跳转或错误
          } else {
            setError(`未能获取 ${paymentMethodType === 'alipay' ? '支付宝' : '微信'} 支付链接，请稍后重试`); //  //  如果 paymentUrl 为空，显示错误
              setShowErrorAlert(true);
              setLoading(false); //  结束加载 (失败 - paymentUrl 为空)
          }
      } catch (error) {
        let errorMessage = `创建 ${paymentMethodType === 'alipay' ? '支付宝' : '微信'} 订单时发生未知错误，请稍后重试`; //  默认错误信息
        if (error.response) {
            //  根据 HTTP 状态码和响应数据，提供更具体的错误提示
            if (error.response.status === 400) {
                errorMessage = error.response.data.message || '请求参数错误，请检查您的输入'; //  例如，后端验证错误
            } else if (error.response.status >= 500) {
                errorMessage = '服务器繁忙，请稍后重试'; //  服务器错误
            } else {
                errorMessage = `创建支付宝订单失败: ${error.response.status} - ${error.response.statusText}`; //  其他 HTTP 错误
            }
          } else if (error.request) {
            errorMessage = '无法连接到服务器，请检查您的网络连接'; //  网络错误
          }
        setError(errorMessage); //  设置错误信息
        setShowErrorAlert(true); //  显示 ErrorAlert
        setLoading(false); //  结束加载
        console.error(`${paymentMethodType === 'alipay' ? '支付宝' : '微信'} 支付创建订单失败:`, error); //  更具体的错误日志
        // alert('支付宝支付创建订单失败: ' + errorMessage); //  可以移除 alert，ErrorAlert 组件已提供提示      
      }
    } else if (paymentMethodType === 'applepay') {
      //  Apple Pay 支付 -  不再直接处理，点击 Apple Pay 按钮会调用 onApplePayButtonClicked
      return; //  阻止 handleSubmit 默认表单提交行为，Apple Pay 支付由 onApplePayButtonClicked 函数处理
    }
  };

  useEffect(() => {
    document.title = '支付 - 责联网--产品大师智库'; // 设置当前页面的标题
    //  当 paymentType 变化时，更新 planId，这里为了演示，假设一次性支付没有 planId，订阅才有 planId
    if (paymentType === 'one-time') {
        setPlanId(null);
    } else if (paymentType === 'subscription' && !planId) {
        //  如果 paymentType 是订阅，但 planId 为空，可以考虑重定向回订阅套餐选择页面，或者显示错误信息
        console.warn('No plan selected for subscription payment.');
        //  为了演示，这里简单设置一个默认的 planId，实际应用中需要根据业务逻辑处理
        // setPlanId('default-subscription-plan-id'); 
    }
  }, [location.pathname, paymentType, planId, location.state?.planId]); //  监听 paymentType 和 location.state?.planId 变化

  return (
    <Container component="main" maxWidth="sm"> {/*  Main container with maxWidth 'sm' */}
    <CssBaseline />
    <Box
        sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}
    >
        <Typography component="h1" variant="h5"> {/*  Title */}
            支付
        </Typography>
        {planName && (
            <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mt: 2, mb: 3 }}> {/* Subtitle with plan info */}
                您选择的套餐: <strong>{planName}</strong> ({planPrice})
            </Typography>
        )}

        <ErrorAlert message={error} onClose={handleCloseErrorAlert} visible={showErrorAlert} /> {/* ErrorAlert component */}

        <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}> {/* FormControl for payment method selection */}
            <FormLabel component="legend" sx={{ textAlign: 'left' }}>选择支付方式</FormLabel> {/* FormLabel for payment method section */}
            <RadioGroup
                aria-label="paymentMethodType"
                name="paymentMethodType"
                value={paymentMethodType}
                onChange={(e) => setPaymentMethodType(e.target.value)}
            > {/* RadioGroup for payment method options */}
                <FormControlLabel value="credit_card" control={<Radio />} label="信用卡支付" /> {/* Credit Card option */}
                <FormControlLabel value="alipay" control={<Radio />} label="支付宝支付" /> {/* Alipay option (disabled) */}
                <FormControlLabel value="wechatpay" control={<Radio />} label="微信支付" /> {/* WeChat Pay option (disabled) */}
                {applePayAvailable && ( // Conditional rendering for Apple Pay option
                    <FormControlLabel
                        value="applepay"
                        control={<Radio />}
                        label="Apple Pay"
                    />
                )}
            </RadioGroup>
        </FormControl>

        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', mt: 3 }}> {/* Form for payment details */}
            {paymentMethodType === 'credit_card' && ( // Conditional rendering for CardElement (Credit Card form)
                <Box sx={{ mb: 3 }}>
                    <CardElement />
                </Box>
            )}

            {paymentMethodType !== 'applepay' && ( // Conditional rendering for "立即支付" button (excluding Apple Pay)
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!stripe || loading}
                    sx={{ mt: 2, mb: 2 }}
                >
                    {loading ? '支付中...' : (paymentType === 'one-time' ? '立即支付' : '订阅')}
                </Button>
            )}

            {applePayAvailable && paymentMethodType === 'applepay' && ( // Conditional rendering for Apple Pay button
                <Box className="apple-pay-button-container" sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        type="button"
                        onClick={onApplePayButtonClicked}
                        className="apple-pay-button"
                        disabled={loading}
                    >
                        <span role="img" aria-label="Apple Pay"></span>
                        {loading ? 'Apple Pay 支付中...' : (paymentType === 'one-time' ? '使用 Apple Pay 支付' : '使用 Apple Pay 订阅')}
                    </Button>
                </Box>
            )}
        </Box>


        {!applePayAvailable && paymentMethodType === 'applepay' && ( // Apple Pay not available message
            <Typography variant="body2" color="orange" align="center" sx={{ mt: 2 }}>
                Apple Pay 在此设备或浏览器上不可用.
            </Typography>
        )}
        {!applePayAvailable && ( // General Apple Pay info message
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                * Apple Pay 仅在 Apple 设备和 Safari 浏览器上可用.
            </Typography>
        )}


        <Box sx={{ mt: 4, textAlign: 'center' }}> {/* Bottom links container */}
            <Typography variant="body2">
                <Link to="/subscription" variant="body2" sx={{ mr: 2 }}>返回套餐选择</Link>
                <Link to="/" variant="body2">返回首页</Link>
            </Typography>
        </Box>
    </Box>
</Container>
);
};

export default PaymentForm;